/**
 * src/pages/ForgotPassword.js
 * "Forgot password?" page — user enters their email to receive a reset link.
 * Always shows a success message regardless of whether the email exists
 * (prevents user enumeration).
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(''); // '' | loading | sent | error
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setStatus('loading');
    try {
      await api.post('/portal/auth/forgot-password', { email: email.trim() });
      setStatus('sent');
    } catch (err) {
      const msg = err.response?.data?.message || 'Something went wrong. Please try again.';
      setError(msg);
      setStatus('error');
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--content-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      <div style={{ width: '100%', maxWidth: '380px' }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
          <div style={{
            width: '36px', height: '36px',
            background: 'var(--accent)',
            borderRadius: '8px'
          }} />
          <div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Process Trails
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Client Portal
            </div>
          </div>
        </div>

        {/* Card */}
        <div className="card">
          <div className="card-body">
            <h1 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>Reset your password</h1>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
              Enter your email address and we'll send you a link to reset your password
            </p>

            {/* Success state */}
            {status === 'sent' && (
              <>
                <div style={{
                  width: '48px', height: '48px',
                  background: '#dbeafe',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '22px',
                  color: '#2563eb'
                }}>
                  &#9993;
                </div>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  textAlign: 'center',
                  lineHeight: '1.6',
                  marginBottom: '24px'
                }}>
                  If an account with that email exists, we've sent a password reset link. Check your inbox.
                </p>
                <div style={{
                  background: '#dbeafe',
                  border: '1px solid #93c5fd',
                  borderRadius: '6px',
                  padding: '12px 14px',
                  marginBottom: '24px',
                  fontSize: '13px',
                  color: '#1e40af',
                  lineHeight: '1.5',
                  textAlign: 'left'
                }}>
                  The link expires in 1 hour. If you don't see the email, check your spam folder.
                </div>
                <Link
                  to="/login"
                  className="btn btn-primary w-full"
                  style={{ justifyContent: 'center', padding: '10px', textDecoration: 'none', display: 'block', textAlign: 'center' }}
                >
                  Back to sign in
                </Link>
              </>
            )}

            {/* Form state */}
            {status !== 'sent' && (
              <>
                {error && (
                  <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="form-group" style={{ marginBottom: '24px' }}>
                    <label className="form-label">Email address</label>
                    <input
                      className="form-input"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      required
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-full"
                    disabled={status === 'loading'}
                    style={{ justifyContent: 'center', padding: '10px' }}
                  >
                    {status === 'loading' ? 'Sending…' : 'Send reset link'}
                  </button>
                </form>

                <p style={{
                  textAlign: 'center',
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  marginTop: '20px',
                  marginBottom: 0
                }}>
                  <Link to="/login" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                    Back to sign in
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
